import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef } from 'react'
import cn from 'classnames'

import saveListing from '../../../sprinkles/saveListing'

const LISTING_CLASS = {
  sale: 'Sale',
  rental: 'Rental'
}

const TEXT_FOR_HIDDEN_ADDRESS = '(undisclosed address)'

const CardContent = ({
  address,
  undisclosedAddressText,
  saved,
  id,
  listingClass,
  townName,
  price,
  currentPricePeriodLabel,
  land,
  beds,
  bathsDisplay,
  acreage,
  sqft,
  byOwner,
  listingBy,
  isActive,
  onSave,
  links
}) => {
  const containerRef = useRef(null)
  const showDetailsByListingClass =
    (listingClass === LISTING_CLASS.sale && !land) ||
    (listingClass === LISTING_CLASS.rental)

  const isAddressHiden = address?.toLowerCase() === TEXT_FOR_HIDDEN_ADDRESS

  const transactionType = listingClass === LISTING_CLASS.rental ? 'Rent' : 'Sale'

  const handleClick = useCallback(e => {
    if (onSave) {
      onSave(e)
    }
  }, [onSave])

  useEffect(() => {
    if (onSave) {
      return
    }
    const node = containerRef.current
    saveListing(node)
  }, [])

  return (
    <div className='CardDesktop-content' ref={containerRef}>
      <div className='CardDesktop-mainContent'>
        <div className='CardDesktop-headerContent'>
          <div className='CardDesktop-priceContent'>
            <div
              className={cn('CardDesktop-price', { 'CardDesktop-price--inactivePrice': !isActive })}
              data-sprinkle='update-viewed-listings'
              data-listing-id={id}
              suppressHydrationWarning
            >
              {price}
            </div>
            {listingClass === LISTING_CLASS.rental && (
              <p className='CardDesktop-period'>{`(${currentPricePeriodLabel})`}</p>
            )}
          </div>
          <div className='CardDesktop-saveAction' data-sprinkle='save-listing'>
            <button
              className={`Button Button--saveLink${ saved ? ' isActive' : ''}`}
              data-id={id}
              data-listing-class={listingClass}
              onClick={handleClick}
              {...(links
                ? {
                  'data-save-listing-button': true,
                  'data-url': links.save_action?.url,
                  'data-method': links.save_action?.method
                }
                : {})}
            />
          </div>
        </div>

        <div className='CardDesktop-detailInfo'>
          <ul className='CardDesktop-detailGroup'>
            {showDetailsByListingClass && (
              <React.Fragment>
                <li className='CardDesktop-detailItem'>
                  <span className='CardDesktop-itemNumber'>{beds}</span>
                  <span className='CardDesktop-itemUnit'>Bd</span>
                </li>
                <li className='CardDesktop-detailItem'>
                  <span className='CardDesktop-itemNumber'>{bathsDisplay}</span>
                  <span className='CardDesktop-itemUnit'>Ba</span>
                </li>
              </React.Fragment>
            )}
            {acreage && (
              <li className='CardDesktop-detailItem'>
                <span className='CardDesktop-itemNumber'>{acreage}</span>
                <span className='CardDesktop-itemUnit'>Acr</span>
              </li>
            )}
            {showDetailsByListingClass && sqft && (
              <li className='CardDesktop-detailItem'>
                <span className='CardDesktop-itemNumber'>{sqft}</span>
                <span className='CardDesktop-itemUnit'>Sq Ft</span>
              </li>
            )}
          </ul>
        </div>

        <div className='CardDesktop-addressContent'>
          {(address && !isAddressHiden) ? (
            <React.Fragment>
              <h2 className='CardDesktop-region'>
                {townName}
              </h2>
              <div className='CardDesktop-circular' />
              <div className='CardDesktop-address'>
                {address}
              </div>
            </React.Fragment>
          ) : (
            <p className='CardDesktop-undisclosedAddress'>{undisclosedAddressText}</p>
          )}
        </div>
      </div>

      <div className='CardDesktop-listingBy withEllipsis'>
        {byOwner ? `For ${transactionType} By Owner` : listingBy}
      </div>
    </div>
  )
}

CardContent.propTypes = {
  id: PropTypes.number,
  address: PropTypes.string,
  undisclosedAddressText: PropTypes.string,
  townName: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentPricePeriodLabel: PropTypes.string,
  saved: PropTypes.bool,
  land: PropTypes.bool,
  beds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bathsDisplay: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  acreage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sqft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  keyAmenityLabel: PropTypes.string,
  byOwner: PropTypes.bool,
  listingBy: PropTypes.string,
  listingClass: PropTypes.string,
  isActive: PropTypes.bool,
  links: PropTypes.shape({
    save_action: PropTypes.shape({
      url: PropTypes.string,
      method: PropTypes.string
    })
  }),
  onSave: PropTypes.func
}

export default CardContent
